// src/components/EFTPaymentModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';

import { db } from '../../firebase';
import {
  collection,
  query,
  orderBy,
  getDocs,
  getDoc,
  doc,
  
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

export default function EFTPaymentModal({
  open,
  onClose,
  vendorId,
  vendorName,
  paymentMethod = 'eft'     
}) {
  const navigate = useNavigate();
  const auth = getAuth();

  // form state
  const [bankAccount, setBankAccount] = useState('');
  const [date, setDate] = useState(() =>
    new Date().toISOString().substr(0, 10)
  );
  const [memo, setMemo] = useState('');
  const [authorized, setAuthorized] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [rows, setRows] = useState([]);

  // Fetch all work orders + tenant address
  useEffect(() => {
    if (!open) return;
    const fetchJobs = async () => {
      try {
        const q = query(
          collection(db, 'workOrders'),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);

        const list = await Promise.all(
          snap.docs.map(async d => {
            const data = d.data();
            let address = 'Unknown address';
            if (data.tenantUid) {
              const userSnap = await getDoc(
                doc(db, 'users', data.tenantUid)
              );
              if (userSnap.exists()) {
                address = userSnap.data().address || address;
              }
            }
            return {
              id: d.id,
              label: `${data.subject || d.id} – ${address}`
            };
          })
        );
        setJobs(list);
      } catch (err) {
        console.error('Error fetching jobs:', err);
      }
    };
    fetchJobs();
  }, [open]);

  // Prefill rows when a job is selected
  useEffect(() => {
    if (!selectedJob) {
      setRows([]);
      return;
    }
    const job = jobs.find(j => j.id === selectedJob);
    setRows([
      {
        propertyOrCompany: vendorName,
        unit: '1',
        account: 'Repairs',
        description: job.label,
        refNo: job.id,
        amount: ''
      }
    ]);
  }, [selectedJob, jobs, vendorName]);

  const handleAddRow = () => {
    setRows(r => [
      ...r,
      {
        propertyOrCompany: '',
        unit: '',
        account: 'Repairs',
        description: '',
        refNo: '',
        amount: ''
      }
    ]);
  };

  const handleRowChange = (i, field, val) => {
    setRows(r => {
      const copy = [...r];
      copy[i][field] = val;
      return copy;
    });
  };

  const total = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
  const formattedDate = date;

  const handleContinue = () => {
    const paymentAmount = total;
    const fee =
      paymentMethod === 'instant'
        ? +(paymentAmount * 0.03).toFixed(2)
        : 5;
    navigate('/pm/maintenance/reviewPayment', {
      state: {
        vendorId,
        jobId: selectedJob,
        vendorName,
        paymentAmount,
        fee,
        total: paymentAmount,
        formattedDate,
        paymentMethod
      }
    });
    onClose();
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={onClose}>
      <DialogTitle>{paymentMethod === "eft" ? 'Electronic' : 'Instant'} Funds transfer</DialogTitle>
      <DialogContent dividers>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <FormControl fullWidth>
            <InputLabel>Bank account</InputLabel>
            <Select
              value={bankAccount}
              label="Bank account"
              onChange={e => setBankAccount(e.target.value)}
            >
              <MenuItem value="operating">Operating Account</MenuItem>
              <MenuItem value="reserves">Reserves Account</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Date"
            type="date"
            fullWidth
            value={date}
            onChange={e => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <FormControl fullWidth>
            <InputLabel>Job</InputLabel>
            <Select
              value={selectedJob}
              label="Job"
              onChange={e => setSelectedJob(e.target.value)}
            >
              {jobs.map(j => (
                <MenuItem key={j.id} value={j.id}>
                  {j.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Pay To"
            fullWidth
            value={vendorName}
            disabled
          />

          <TextField
            label="Memo"
            fullWidth
            multiline
            value={memo}
            onChange={e => setMemo(e.target.value)}
          />
        </Box>

        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={authorized}
              onChange={e => setAuthorized(e.target.checked)}
            />
          }
          label="By authorizing, you approve a debit from the selected account."
        />

        <Typography variant="subtitle1" mt={3}>
          Apply amounts to accounts
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Property or Company</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Ref. No.</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i}>
                {[
                  'propertyOrCompany',
                  'unit',
                  'account',
                  'description',
                  'refNo'
                ].map(field => (
                  <TableCell key={field}>
                    <TextField
                      variant="standard"
                      fullWidth
                      value={r[field]}
                      onChange={e =>
                        handleRowChange(i, field, e.target.value)
                      }
                    />
                  </TableCell>
                ))}
                <TableCell>
                  <TextField
                    variant="standard"
                    type="number"
                    fullWidth
                    value={r.amount}
                    onChange={e =>
                      handleRowChange(i, 'amount', e.target.value)
                    }
                  />
                </TableCell>
                <TableCell />
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={6}>
                <Button startIcon={<AddIcon />} onClick={handleAddRow}>
                  Add row
                </Button>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} align="right">
                <Typography fontWeight={600}>Total</Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={600}>
                  ${total.toFixed(2)}
                </Typography>
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>

        <Typography variant="subtitle1" mt={3}>
          Attachments (max 10 files, 20MB each)
        </Typography>
        <input type="file" multiple style={{ marginTop: 8 }} />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          disabled={!authorized || rows.length === 0 || total <= 0}
          onClick={handleContinue}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}